import type {
  LegalAcceptanceDto,
  LegalDocumentDto,
} from '@nomiss/sdk';
import {
  ApiRequestError,
  executeSdkRawRequest,
} from './api-request.util';
import { legalApi, resolveAccessToken } from './sdk-client';

export type LegalSection = {
  slug: string;
  heading: string;
  body: string;
};

export type LegalDocument = {
  kind: 'terms' | 'privacy';
  version: number;
  effectiveDate: string;
  title: string;
  intro: string | null;
  sections: LegalSection[];
  language: string;
};

export type LegalAcceptance = {
  kind: 'terms' | 'privacy';
  version: number;
  documentId: string;
  acceptedAt: string;
};

export type LegalAcceptanceInput = {
  kind: 'terms' | 'privacy';
  version?: number;
};

export class LegalError extends ApiRequestError {
  constructor(status: number, message: string) {
    super(status, message, 'LegalError');
  }
}

function authInitOverrides(
  accessToken?: string | null,
): RequestInit | undefined {
  const token = accessToken ?? resolveAccessToken();
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
}

function normalizeLegalDocument(payload: LegalDocumentDto): LegalDocument {
  return {
    kind: payload.kind,
    version: payload.version,
    effectiveDate: payload.effectiveDate,
    title: payload.title,
    intro:
      typeof payload.intro === 'string'
        ? payload.intro
        : payload.intro === null
          ? null
          : null,
    sections: (payload.sections ?? []).map(section => ({
      slug: section.slug,
      heading: section.heading,
      body: section.body,
    })),
    language: payload.language,
  };
}

function normalizeLegalAcceptance(
  payload: LegalAcceptanceDto,
): LegalAcceptance {
  return {
    kind: payload.kind,
    version: payload.version,
    documentId: payload.documentId,
    acceptedAt: payload.acceptedAt,
  };
}

export async function getLegalDocument(
  kind: 'terms' | 'privacy',
  lang?: string,
): Promise<LegalDocument> {
  const data = await executeSdkRawRequest<LegalDocumentDto>({
    request: () =>
      legalApi.legalControllerGetDocumentV1Raw({
        kind,
        lang,
      }),
    fallback: 'Failed to load legal document',
    ErrorClass: LegalError,
  });

  return normalizeLegalDocument(data);
}

export async function getMyLegalAcceptances(
  accessToken?: string | null,
): Promise<LegalAcceptance[]> {
  const data = await executeSdkRawRequest<LegalAcceptanceDto[]>({
    request: () =>
      legalApi.legalControllerListAcceptancesV1Raw(
        authInitOverrides(accessToken),
      ),
    fallback: 'Failed to load legal acceptances',
    ErrorClass: LegalError,
  });

  return (data ?? []).map(normalizeLegalAcceptance);
}

export async function acceptLegalDocuments(
  acceptances: LegalAcceptanceInput[],
  accessToken?: string | null,
): Promise<LegalAcceptance[]> {
  const data = await executeSdkRawRequest<LegalAcceptanceDto[]>({
    request: () =>
      legalApi.legalControllerAcceptDocumentsV1Raw(
        { acceptLegalDocumentsDto: { acceptances } },
        authInitOverrides(accessToken),
      ),
    fallback: 'Failed to record legal acceptance',
    ErrorClass: LegalError,
  });

  return (data ?? []).map(normalizeLegalAcceptance);
}

export function hasFullLegalAcceptance(
  acceptances: LegalAcceptance[],
): boolean {
  const kinds = new Set(acceptances.map(item => item.kind));
  return kinds.has('terms') && kinds.has('privacy');
}
